import React, { useState } from "react";
import Tab from "react-bootstrap/Tab";
import Col from "react-bootstrap/Col";
import Nav from "react-bootstrap/Nav";
import "lazysizes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  StyledNav,
  StyledTabPanes,
  StyledRow,
} from "./FeaturesTabsHelpersCity";
import {
  ISubFeatureTabLinks,
  ISubFeatureTabPanes,
  IFeatureImage,
  IFeatureTabPanes,
  IFeaturesTabs,
} from "./FeaturesTabsPropsCity";
import { ILocationInterestFeature } from "../../../templates/InterestLocation/InterestLocationProps";

const baseUrl =
  process.env.GATSBY_CORE_CONTENT_BASE_URL ||
  "https://rlhcorestg.prod.acquia-sites.com";

const SubFeatureTabLinks: ISubFeatureTabLinks = ({ subFeatures }) => {
  return (
    <div className="nested-tabs">
      {subFeatures.map((subfeature) => {
        const subTabKey = subfeature.field_feature_tab_title
          .toLowerCase()
          .trim();
        return (
          <Nav.Item key={subfeature.field_feature_tab_title}>
            <Nav.Link eventKey={subTabKey}>
              {subfeature.field_feature_tab_title}
            </Nav.Link>
          </Nav.Item>
        );
      })}
    </div>
  );
};

const SubFeatureTabPanes: ISubFeatureTabPanes = ({ features }) => {
  return features.map((subfeature) => {
    const subTabKey = subfeature.field_feature_tab_title
      .toLowerCase()
      .trim()
      ?.replace(/ /g, "-");
    let subFeatureDesc = null;
    if (subfeature.field_feature_description) {
      subFeatureDesc = subfeature.field_feature_description.value;
    }
    let image = null;
    if (
      subfeature.relationships?.field_feature_image_media?.relationships
        ?.field_media_image_5
    ) {
      const rawUrl =
        subfeature.relationships.field_feature_image_media.relationships
          .field_media_image_5.uri.url;
      const fullUrl = rawUrl.startsWith("http")
        ? rawUrl
        : `${baseUrl}${rawUrl}`;
      image = {
        alt: "",
        url: fullUrl,
      };
    }
    return (
      <Tab.Pane as="div" key={subTabKey} eventKey={subTabKey}>
        {image ? <FeatureImage image={image} /> : ""}
        <h2 className="feature-title">{subfeature.field_feature_title}</h2>
        <div dangerouslySetInnerHTML={{ __html: subFeatureDesc }} />
      </Tab.Pane>
    );
  });
};

const FeatureImage: IFeatureImage = ({ image }) => {
  if (image) {
    return (
      <img
        className={"feature-image lazyload"}
        data-sizes="auto"
        data-src={image.url}
        alt={image.alt}
      />
    );
  }
  return null;
};

const FeatureTabPanes: React.FC<IFeatureTabPanes> = ({ features }) => {
  return features.map((feature) => {
    const tabKey = feature.field_feature_tab_title
      .toLowerCase()
      .trim()
      ?.replace(/ /g, "-");
    const subFeatures = feature.relationships.field_sub_feature
      ? feature.relationships.field_sub_feature
      : [];
    let featureDesc = null;
    if (feature.field_feature_description) {
      featureDesc = feature.field_feature_description.value;
    }
    let image = null;
    if (
      feature.relationships?.field_feature_image_media?.relationships
        ?.field_media_image_5
    ) {
      const rawUrl =
        feature.relationships.field_feature_image_media.relationships
          .field_media_image_5.uri.url;
      const fullUrl = rawUrl.startsWith("http")
        ? rawUrl
        : `${baseUrl}${rawUrl}`;
      image = {
        alt: "",
        url: fullUrl,
      };
    }
    return (
      <React.Fragment key={tabKey}>
        <Tab.Pane eventKey={tabKey}>
          {image ? <FeatureImage image={image} /> : ""}
          <h2 className="feature-title">{feature.field_feature_title}</h2>
          <div dangerouslySetInnerHTML={{ __html: featureDesc }} />
        </Tab.Pane>
        {subFeatures ? <SubFeatureTabPanes features={subFeatures} /> : ""}
      </React.Fragment>
    );
  });
};

const FeaturesTabsCity: IFeaturesTabs = ({ features }) => {
  const [activeTab, setActiveTab] = useState(
    features[0].field_feature_tab_title.toLowerCase().trim()
  );

  const handleClickTab = (event: any) => {
    const key = event.currentTarget.dataset.tab;
    setActiveTab(key);
  };

  const renderFeatureTabLinks = () => {
    return features.map((feature: ILocationInterestFeature) => {
      const tabKey = feature.field_feature_tab_title.toLowerCase().trim();
      const activeClass = activeTab === tabKey ? "active" : "";
      const subFeatures = feature.relationships.field_sub_feature
        ? feature.relationships.field_sub_feature
        : [];
      return (
        <Nav.Item
          onClick={handleClickTab}
          data-tab={tabKey?.replace(/ /g, "-")}
          key={feature.field_feature_tab_title}
          className={`${activeClass} ${
            feature.relationships.field_sub_feature !== null
              ? "has-children"
              : ""
          }`}
        >
          <Nav.Link eventKey={tabKey?.replace(/ /g, "-")}>
            {feature.field_feature_tab_title}
            {subFeatures.length > 0 && <FontAwesomeIcon icon="chevron-down" />}
          </Nav.Link>
          {subFeatures.length > 0 && (
            <SubFeatureTabLinks subFeatures={subFeatures} />
          )}
        </Nav.Item>
      );
    });
  };

  const defaultTab = features[0].field_feature_tab_title
    .toLowerCase()
    .trim()
    ?.replace(/ /g, "-");
  return (
    <Tab.Container defaultActiveKey={defaultTab} id="feature-tabs">
      <StyledRow>
        <Col lg={4} className="featuresCol">
          <StyledNav>{renderFeatureTabLinks()}</StyledNav>
        </Col>
        <Col lg={{ span: 7, offset: 1 }}>
          <StyledTabPanes>
            <Tab.Content>
              <FeatureTabPanes features={features} />
            </Tab.Content>
          </StyledTabPanes>
        </Col>
      </StyledRow>
    </Tab.Container>
  );
};

export default FeaturesTabsCity;
